import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import React, { type JSX } from "react";
import { connect } from "react-redux";
import { CSSTransition } from "react-transition-group";

import { RichText } from "../../../common/RichText";
import { IOptionValues, IProduct } from "../../../models/catalogue.interfaces";
import { IWebPageURL } from "../../../models/nominals";
import { TDispatchMapper, TStateMapper } from "../../reducers.interfaces";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { defaults } from "../defaults";
import { Dispatchers } from "../dispatchers";
import { IModularConfiguratorOptionSet } from "../models.interfaces";
import { rootProductSelector } from "../selectors";
import { ConfiguratorPanel } from "./ConfiguratorPanel";
import { ConfiguratorPanelTrigger } from "./ConfiguratorPanelTrigger";
import { PanelRootProductSelectorOption } from "./PanelRootProductSelectorOption";

import styles from "./PanelRootProductSelector.module.scss";

interface IOwnProps {
    optionSet: IModularConfiguratorOptionSet;
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    financingLink?: IWebPageURL;
}

interface IReduxProps {
    rootProducts: IProduct[];
    selectedRootProduct: IProduct | null;
    optionValues: IOptionValues;
}

interface IDispatchProps {
    onSelectRootProduct: (product: IProduct) => void;
}

interface IProps extends IOwnProps, IReduxProps, IDispatchProps {}

interface IState {
    isPanelOpen: boolean;
}

// TODO(ssr, LEVEL 8 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 8/10 due to 265 LOC, body-scroll-lock integration with refs, CSSTransition animation management, complex panel state lifecycle (open/close with scroll handling), Redux integration, mobile width detection, and trigger content building logic.
class PanelRootProductSelectorComponent extends React.PureComponent<
    IProps,
    IState
> {
    private readonly panelContainerRef = React.createRef<HTMLDivElement>();
    public state: IState = {
        isPanelOpen: false,
    };

    componentWillUnmount() {
        clearAllBodyScrollLocks();
    }

    private readonly onClosePanel = () => {
        // `enableBodyScroll` should run before setState `isPanelOpen: false`,
        // so inline style `position: fixed` from `<body>` will be removed first before removing `.configurator-panel-open` from `<body>`.
        // This way can avoid the issue of the page jumping to the top when panel is opened/closed
        enableBodyScroll(this.panelContainerRef.current as HTMLElement);
        this.setState(
            {
                isPanelOpen: false,
            },
            () => {
                // Wait a few seconds to remove inline style `position: fixed` before removing `.configurator-panel-open` from `<body>`.
                setTimeout(() => {
                    document.body.classList.remove("configurator-panel-open");
                }, 500);
            },
        );
    };

    private readonly onOpenPanel = () => {
        this.setState(
            {
                isPanelOpen: true,
            },
            () => {
                if (window.innerWidth < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE) {
                    disableBodyScroll(
                        this.panelContainerRef.current as HTMLElement,
                        {
                            allowTouchMove: (el) => {
                                while (el && el !== document.body) {
                                    if (
                                        el.getAttribute(
                                            "data-body-scroll-lock-ignore",
                                        ) !== null
                                    ) {
                                        return true;
                                    }

                                    el = el.parentElement as
                                        | HTMLElement
                                        | Element;
                                }

                                return false;
                            },
                        },
                    );
                } else {
                    enableBodyScroll(
                        this.panelContainerRef.current as HTMLElement,
                    );
                }
            },
        );
    };

    private buildPanelTriggerContent(): React.ReactNode {
        if (!this.props.selectedRootProduct) {
            return null;
        }
        if (
            this.props.selectedRootProduct.attributes
                .configurator_panel_trigger_title
        ) {
            return (
                <RichText
                    inline={true}
                    html={`${this.props.selectedRootProduct.attributes.configurator_panel_trigger_title.value}`}
                />
            );
        }
        if (this.props.selectedRootProduct.description) {
            // TODO(i18n): Don't assume word order or punctuation. Use jt or complete translated sentence: jt`${description} (${title})`
            return (
                <>
                    <RichText
                        inline={true}
                        html={this.props.selectedRootProduct.description}
                    />
                    (
                    {this.props.selectedRootProduct.title.replace(
                        "TEMPUR-",
                        "",
                    )}
                    )
                </>
            );
        }
        return this.props.selectedRootProduct.title;
    }

    render() {
        if (
            this.props.rootProducts.length <= 1 ||
            !this.props.selectedRootProduct
        ) {
            return null;
        }
        return (
            <>
                <CSSTransition
                    in={this.state.isPanelOpen}
                    timeout={500}
                    classNames="configurator__panel-"
                    unmountOnExit={true}
                    nodeRef={this.panelContainerRef}
                >
                    <div ref={this.panelContainerRef}>
                        <ConfiguratorPanel
                            prefixNameCapped={
                                this.props.optionSet.root_product_selector.label
                            }
                            onClosePanel={this.onClosePanel}
                            panelClass={"model"}
                            chatLinkComponent={this.props.chatLinkComponent}
                            liveChatHeader={this.props.liveChatHeader}
                            enableChat={this.props.optionSet.layout.enable_chat}
                            radioSelector={
                                <div className="configurator__radio-container">
                                    <fieldset>
                                        <legend className="ada-screenreader-only">
                                            {
                                                this.props.optionSet
                                                    .root_product_selector.label
                                            }
                                        </legend>
                                        <ul className={styles.container}>
                                            {this.props.rootProducts.map(
                                                (rootProduct) => (
                                                    <PanelRootProductSelectorOption
                                                        key={`${rootProduct.id}`}
                                                        optionSet={
                                                            this.props.optionSet
                                                        }
                                                        rootProduct={
                                                            rootProduct
                                                        }
                                                        selectedRootProduct={
                                                            this.props
                                                                .selectedRootProduct!
                                                        }
                                                        enableRatings={
                                                            this.props.optionSet
                                                                .root_product_selector
                                                                .enable_ratings
                                                        }
                                                        onSelect={() => {
                                                            this.props.onSelectRootProduct(
                                                                rootProduct,
                                                            );
                                                            this.onClosePanel();
                                                        }}
                                                        onClick={
                                                            this.onClosePanel
                                                        }
                                                    />
                                                ),
                                            )}
                                        </ul>
                                    </fieldset>
                                </div>
                            }
                        />
                    </div>
                </CSSTransition>
                <ConfiguratorPanelTrigger
                    prefixNameCapped={
                        this.props.optionSet.root_product_selector.label
                    }
                    onOpenPanel={this.onOpenPanel}
                >
                    {this.buildPanelTriggerContent()}
                </ConfiguratorPanelTrigger>
            </>
        );
    }
}

const mapStateToProps: TStateMapper<"configurator", IReduxProps, IOwnProps> = (
    rootState,
    ownProps,
) => {
    const state = rootState.configurator || defaults;
    return {
        rootProducts: state.entities.rootProducts,
        selectedRootProduct: rootProductSelector(state),
        optionValues: state.ui.optionValues,
        ...ownProps,
    };
};

const mapDispatchToProps: TDispatchMapper<IDispatchProps> = (dispatch) => {
    const dispatchers = new Dispatchers(dispatch);
    return {
        onSelectRootProduct: (product) => {
            dispatchers.setSelectedRootProduct(product);
        },
    };
};

export const PanelRootProductSelector = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PanelRootProductSelectorComponent);
